<template>
  <div class="grid grid-pad box-size subs-today-grid">
    <div class="subs-today text-dark">
      <div class="subs-today__header">
        <h1 v-if="isSubsToday">{{ substitutesToday.length }} {{ i18n[locale]['subs'] }}, <span
          class="headline-date">{{ formattedDayDate }}</span>
        </h1>
        <h1 v-else>{{ absentToday.length }} {{ i18n[locale]['absent'] }}, <span
          class="headline-date">{{ formattedDayDate }}</span>
        </h1>
        <div class="subs-today__header__right no-print">
          <button @click="setToday"
                  :class="{'bttn--disabled': getIsToday}"
                  class="date-buttons marg-5-right">
            {{ i18n[locale]['today'] }}
          </button>
          <button @click="prevDay" class="date-buttons marg-5-right">
            <span class="icon-arrow-left4"></span>
          </button>
          <DatePicker
            :date="date"
            @changed="setDate">
          </DatePicker>
          <button @click="nextDay" class="marg-5-left date-buttons">
            <span class="icon-arrow-right4"></span>
          </button>
          <button class="marg-1-left" v-if="isSubsToday" @click="asyncLoadXLSXAndDownload">{{
              i18n[locale]['download']
            }}
          </button>
          <button class="marg-1-left" @click="print"><span class="icon-printer3"></span></button>
          <button class="marg-1-left marg-1-right" @click="startSlider">
            <span class="icon-shrink7" v-if="isSlider"></span>
            <span class="icon-enlarge2" v-else></span>
          </button>
        </div>
      </div>
      <Loader v-if="isLoading"></Loader>
      <h2 class="text-explainer" v-if="isSubsToday && !isLoading && substitutesToday.length < 1">{{
          i18n[locale]['no_subs_today']
        }}
      </h2>
      <h2 class="text-explainer" v-if="!isSubsToday && !isLoading && absentToday.length < 1">{{
          i18n[locale]['no_one_absent_today']
        }}
      </h2>
      <div class="users-today__container" v-else>
        <div class="subs-grid" v-for="(userRow, key) in usersInArraysSlider" v-if="!isSlider || key === theSliderIndex">
          <div class="subs-today__sub" v-for="user in userRow" :key="user.id" :data-user-id="user.id">
            <h3>{{ user.name }}</h3>
            <p class="subs-today__sub__absence" v-for="absence in user.absence">{{ absence.title }} -
              {{ absence.timeFormatted }}
            </p>
            <p class="text-explainer marg-1-top" v-if="user.events.length < 1">{{
                i18n[locale]['no_lessons_effected']
              }}</p>
            <table class="d-table" v-if="user.events.length > 0">
              <thead>
              <th>{{ i18n[locale]['course'] }}</th>
              <th>{{ i18n[locale]['grade'] }}</th>
              <th v-if="isShowRooms">{{ i18n[locale]['room'] }}</th>
              <th :class="{'d-table__subs-for': !isShowRooms}">
                <template v-if="!isSubsToday">{{ i18n[locale]['sub'] }}</template>
                <template v-else>{{ i18n[locale]['sub_for'] }}</template>
              </th>
              </thead>
              <tbody>
              <tr v-for="event in user.events" :key="event.id">
                <td>
                  <div class="subs-today__event-title">{{ event.title }}</div>
                  {{ event.timeFormatted }}
                </td>
                <td>{{ event.grades }}</td>
                <td v-if="isShowRooms">{{ event.rooms }}</td>
                <td>
                  <template v-if="!isSubsToday">{{ event.substitute }}</template>
                  <template v-else>{{ event.substitute_for }}</template>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="users-today__slider-pager marg-2-top" v-if="isSlider">
          <p>{{ theSliderIndex + 1 }} / {{ userInRows.length }}</p>
          <div class="d-progress" v-if="userInRows.length > 1">
            <div class="d-progress__bar" :style="{'width': animatePercentage}"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DatePicker from '../datepicker/DateSelector'
import Loader from '../common/Loader'
import Moment from 'moment'
import {extendMoment} from 'moment-range'
import docendo from '../../docendo'

const moment = extendMoment(Moment)
let XLSX = null

const I18N = {
  en: {
    subs_today: 'Substitutes today',
    today: 'Today',
    download: 'Download to Excel',
    no_subs_today: 'No substitutes today',
    course: 'COURSE',
    grade: 'GROUP',
    room: 'ROOM',
    sub_for: 'SUBSTITUTE FOR',
    not_on_school: 'Not on school',
    subs: 'substitutes',
    sub: 'substitute',
    absent: 'absent',
    no_one_absent_today: 'No one absent',
    no_lessons_effected: 'No lessons are affected by the absence',
    absence: 'Absence'
  },
  da: {
    subs_today: 'Dagens vikarer',
    today: 'I dag',
    no_subs_today: 'Ingen vikarer denne dag',
    download: 'Download til Excel',
    course: 'FAG',
    grade: 'KLASSE',
    room: 'LOKALE',
    sub_for: 'VIKAR FOR',
    not_on_school: 'Ikke på skolen',
    subs: 'vikarer',
    sub: 'vikar',
    absent: 'fraværende',
    no_one_absent_today: 'Ingen fraværende',
    no_lessons_effected: 'Ingen lektioner påvirkes af fravær',
    absence: 'Fravær'
  },
  nb: {
    subs_today: 'Dagens vikarer',
    today: 'I dag',
    no_subs_today: 'Ingen vikarer i dag',
    download: 'Last ned som Excel-fil',
    course: 'FAG',
    grade: 'KLASSE',
    room: 'ROM',
    sub_for: 'VIKAR FOR',
    not_on_school: 'Ikke på skolen',
    subs: 'vikarer',
    sub: 'vikar',
    absent: 'fraværende',
    no_one_absent_today: 'Ingen fraværende',
    no_lessons_effected: 'Ingen leksjoner påvirkes av fraværet',
    absence: 'Fravær'
  }
}
export default {
  components: {Loader, DatePicker},
  props: {
    isSubsToday: {
      type: Boolean,
      default: true
    },
    isPublic: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isDownloading: false,
      absentToday: [],
      isLoadingAbsence: false,
      isSlider: false,
      theSliderIndex: 0,
      theSliderInterval: null,
      theAnimateProgress: 0,
      theAnimateProgressInterval: null
    }
  },
  computed: {
    schoolUuid () {
      return window.SCHOOL && window.SCHOOL.id ? window.SCHOOL.id : this.$route.params.school_uuid
    },
    urlLocale () {
      const url = window.location.host
      let locale = 'en'
      if (url.indexOf('.dk') > -1) {
        locale = 'da'
      }
      if (url.indexOf('.no') > -1) {
        locale = 'nb'
      }
      return locale
    },
    locale () {
      return window.USER && window.USER.language ? window.USER.language : this.urlLocale
    },
    isShowRooms () {
      return this.locale === 'en'
    },
    i18n () {
      return I18N
    },
    isLoadingSubs () {
      return this.$store.state.subs.dataIsLoading
    },
    isLoading () {
      return this.isLoadingSubs || this.isLoadingAbsence
    },
    getIsToday () {
      moment.locale(this.locale)
      return moment(this.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
    },
    date () {
      return moment(this.$store.state.subs.date).format('YYYY-MM-DD')
    },
    formattedDayDate () {
      moment.locale(this.locale)
      return moment(this.date).format('dddd') + ' ' + moment(this.date).format('LL')
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    allKlassCalendarsInAHash () {
      return this.$store.getters.allKlassCalendarsInHash
    },
    allEventsInHash () {
      return this.$store.state.subs.allEventsInAHash
    },
    allUsersInCalIdHash () {
      return this.$store.getters.allUsersInCalIdHash
    },
    allUsersInHash () {
      return this.$store.getters.allUsersInHash
    },
    allUsersAndEvents () {
      return this.$store.state.subs.allUsersAndEvents
    },
    substitutesToday () {
      return this.$store.getters.substitutesToday
    },
    absentFormatted () {
      return this.absentToday.map(user => {
        const userObj = {
          id: user.user_id,
          user_calendar_id: user.calendar_id,
          name: user.name,
          dayEvents: [],
          dayAbsence: []
        }
        user.events.forEach(event => {
          if (event.event_type === 'absence') {
            userObj.dayAbsence.push(event)
          }
          if (event.event_type !== 'absence' && event.event_type !== 'presence') {
            userObj.dayEvents.push(event)
          }
        })
        return userObj
      })
    },
    usersFormatted () {
      const usersSelected = this.isSubsToday ? this.substitutesToday : this.absentFormatted
      const users = usersSelected.map(user => {
        const eventsArr = this.isSubsToday ? user.dayEvents.filter(event => this.allEventsInHash[event]).map(eventId => this.allEventsInHash[eventId]) : user.dayEvents
        const events = this.isSubsToday ? eventsArr.filter(event => this.isEventAsASubstitute(event, user)) : eventsArr.filter(event => this.doesEventOverLapAbsence(event, user))
        const absence = this.isSubsToday ? user.dayAbsence.filter(event => this.allEventsInHash[event]).map(eventId => this.allEventsInHash[eventId]) : user.dayAbsence
        const absenceFormatted = absence.map(event => {
          const eventStart = moment(event.start).format('LT')
          const eventEnd = moment(event.end).format('LT')
          let eventFormat = {
            title: this.isPublic ? this.i18n[this.locale]['absence'] : event.title,
            startTime: event.start,
            start: eventStart,
            end: eventEnd,
            timeFormatted: eventStart + '-' + eventEnd
          }
          return eventFormat
        })
        const eventsFormatted = events.map(event => {
          const eventStart = moment(event.start).format('LT')
          const eventEnd = moment(event.end).format('LT')
          let eventFormat = {
            title: event.title,
            startTime: event.start,
            start: eventStart,
            end: eventEnd,
            timeFormatted: eventStart + '-' + eventEnd,
            grades: [],
            substitute_for: [],
            substitute: [],
            rooms: []
          }
          event.calendar_events.forEach(calEvent => {
            // Find grades
            if (calEvent.calendar_type === 'klass' && calEvent.name) {
              eventFormat.grades.push(calEvent.name)
            }
            // Find substitute for
            if (calEvent.calendar_id === user.user_calendar_id && calEvent.substitute_for_user_id) {
              const subForUserName = this.allUsersInHash[calEvent.substitute_for_user_id] ? this.allUsersInHash[calEvent.substitute_for_user_id].name : this.i18n[this.locale]['not_on_school']
              eventFormat.substitute_for.push(subForUserName)
            }
            // Find substitute
            if (calEvent.substitute_for_user_id === user.id) {
              eventFormat.substitute.push(calEvent.name)
            }
            // Find rooms
            if (calEvent.calendar_type === 'room' && calEvent.name) {
              eventFormat.rooms.push(calEvent.name)
            }
          })
          eventFormat.grades = eventFormat.grades.sort((a, b) => {
            if (a && b) {
              return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            }
          })
          if (eventFormat.grades.length < 3) {
            eventFormat.grades = eventFormat.grades.join(', ')
          } else {
            let gradesAsString = eventFormat.grades.filter((grade, idx) => idx < 3).join(', ')
            eventFormat.grades = gradesAsString + '...'
          }
          eventFormat.substitute_for = eventFormat.substitute_for.sort((a, b) => {
            if (a && b) {
              return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            }
          }).join(', ')
          eventFormat.substitute = eventFormat.substitute.sort((a, b) => {
            if (a && b) {
              return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            }
          }).join(', ')
          eventFormat.rooms = eventFormat.rooms.sort((a, b) => {
            if (a && b) {
              return a.localeCompare(b, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            }
          }).join(', ')
          return eventFormat
        })
        const userObj = {
          name: user.name ? user.name : this.allUsersInCalIdHash[user.user_calendar_id] ? this.allUsersInCalIdHash[user.user_calendar_id].name : '(' + this.i18n[this.locale]['not_on_school'] + ')',
          events: eventsFormatted.sort((a, b) => moment(a.startTime).unix() - moment(b.startTime).unix()),
          absence: absenceFormatted.sort((a, b) => moment(a.startTime).unix() - moment(b.startTime).unix())
        }
        return userObj
      })
      return this.$helpers.sortArrOnName(users)
    },
    userInRows () {
      const newArr = []
      const arr = JSON.parse(JSON.stringify(this.usersFormatted))
      while (arr.length) newArr.push(arr.splice(0, 8))
      return newArr
    },
    usersInArraysSlider () {
      return this.userInRows
    },
    dataToExcel () {
      const dataArr = [
        {name: this.i18n[this.locale]['subs_today'] + ', ' + this.formattedDayDate},
        {name: ''}
      ]
      this.usersFormatted.forEach(user => {
        dataArr.push({name: user.name})
        dataArr.push({
          name: this.i18n[this.locale]['course'],
          class: this.i18n[this.locale]['grade'],
          sub_for: this.i18n[this.locale]['sub_for'],
          room: this.i18n[this.locale]['room']
        })
        user.events.forEach(event => {
          dataArr.push({
            name: event.title + ', ' + event.timeFormatted,
            class: event.grades,
            sub_for: event.substitute_for,
            room: event.rooms
          })
        })
        dataArr.push({name: ''})
      })
      return dataArr
    },
    animatePercentage () {
      return ((this.theAnimateProgress / 700) * 100) + '%'
    }
  },
  methods: {
    animateProgress () {
      this.theAnimateProgressInterval = window.setInterval(_ => {
        this.theAnimateProgress += 1
      }, 10)
    },
    startSlider () {
      this.isSlider = !this.isSlider
      clearInterval(this.theSliderInterval)
      clearInterval(this.theAnimateProgressInterval)
      if (this.isSlider) {
        this.animateProgress()
        this.theSliderInterval = window.setInterval(_ => {
          if (this.theSliderIndex < this.userInRows.length - 1) {
            this.theSliderIndex++
          } else {
            this.theSliderIndex = 0
          }
          this.theAnimateProgress = 0
        }, 7000)
      } else {
        this.theSliderIndex = 0
      }
    },
    isEventAsASubstitute (event, user) {
      let isSub = false
      event.calendar_events.forEach(calEvent => {
        if (calEvent.calendar_id === user.user_calendar_id && calEvent.substitute) {
          isSub = true
        }
      })
      return isSub
    },
    doesEventOverLapAbsence (event, user) {
      let overlaps = false
      for (let i = 0; i < user.dayAbsence.length; i++) {
        let absence = user.dayAbsence[i]
        const range1 = moment.range(absence.start, absence.end)
        const range2 = moment.range(event.start, event.end)
        if (range1.overlaps(range2)) {
          overlaps = true
        }
      }
      return overlaps
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.download()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        let intervalTimer = setInterval(_ => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.download()
          }
        }, 500)
      }
    },
    download () {
      const data = XLSX.utils.json_to_sheet(this.dataToExcel, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, data, 'data')
      XLSX.writeFile(wb, 'docendo_subs_today.xlsx')
      this.isDownloading = false
    },
    setDate (date) {
      this.$store.commit('setSubDate', date)
      this.getData(date)
    },
    prevDay () {
      const date = moment(this.date).subtract(1, 'days').format('YYYY-MM-DD')
      this.setDate(date)
    },
    nextDay () {
      const date = moment(this.date).add(1, 'days').format('YYYY-MM-DD')
      this.setDate(date)
    },
    setToday () {
      const date = moment().format('YYYY-MM-DD')
      this.setDate(date)
    },
    print () {
      window.print()
    },
    getData (date) {
      if (this.isSubsToday) {
        this.$store.dispatch('getAllDayEvents')
      } else {
        this.getAbsence(date)
      }
    },
    getAbsence (date) {
      this.isLoadingAbsence = true
      docendo.absenceForDate(this.schoolUuid, date, success => {
        this.isLoadingAbsence = false
        this.absentToday = success && success.length > 1 ? JSON.parse(success) : []
      }, failure => {
        console.log('failure')
      })
    }
  },
  beforeMount () {
    if (window.location.href.indexOf('?kiosk') > -1) {
      this.startSlider()
    }
  },
  mounted () {
    if (this.isSubsToday) {
      this.$store.dispatch('getAllUsers')
      this.$store.dispatch('getAllDayEvents')
    } else {
      this.getAbsence(moment())
    }
  }
}
</script>

<style scoped>
.content_container .grid.subs-today-grid {
  max-width: 1600px;
  min-width: 0;
}

.subs-today__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.subs-today__header__right {
  display: flex;
  align-items: stretch;
}

.subs-today__header__right button {
  height: auto;
  white-space: nowrap;
}

.subs-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
}

.subs-today .subs-today__sub {
  flex: 1;
  padding: 1rem;
  min-width: 330px;
  max-width: calc(25% - 1rem);
  box-shadow: 0 1px 2px rgb(0 0 0 / 7%);
  background: #fff;
  overflow: hidden;
  border-radius: 4px;
  vertical-align: top;
}

.subs-today__sub tr:nth-child(even) {
  background-color: #f9fafb;
}

@media print {
  .subs-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
  }

  .subs-today .subs-today__sub {
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 1rem;
    break-inside: avoid !important;
    font-size: 12px;
  }
}

@media print {
  .subs-today-grid {
    padding-top: 0 !important;
  }

  .subs-today-grid h1,
  .subs-today-grid .headline-date {
    font-size: 14px;
  }

  .subs-today__header {
    margin-bottom: 0;
  }

  .subs-today .subs-grid {
    display: block;
  }

  .subs-today .subs-today__sub {
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 1rem !important;
    font-size: 12px;
    page-break-inside: avoid;
    width: 100%;
    max-width: 100%;
    margin-bottom: 1rem;
  }

  .subs-today .subs-today__sub,
  .subs-today .subs-today__sub .d-table,
  .subs-today .subs-today__sub .d-table thead,
  .subs-today .subs-today__sub .d-table tbody,
  .subs-today .subs-today__sub .d-table tbody tr,
  .subs-today .subs-today__sub .d-table tbody tr td {
    page-break-inside: avoid !important;
  }

  .subs-today .subs-today__sub .d-table__subs-for {
    min-width: auto;
  }

  .d-progress {
    display: none;
  }
}

@media print and (orientation: landscape) {
  .subs-today .subs-today__sub {
    width: 23.5% !important;
    min-width: auto !important;
    max-width: 23.5% !important;
    margin-right: 1rem;
    display: inline-block !important;
  }
}

.subs-today__sub h3 {
  font-size: 1.35rem;
}

.subs-today__sub p.subs-today__sub__absence {
  font-size: 1rem;
}

.subs-today__sub .d-table {
  margin-top: 1rem;
}


.d-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #d8d8d8;
  font-size: 12px;
}

.d-table thead {
  border-bottom: 1px solid #d8d8d8;
  background: #f6f9fc;
  color: #1a1f36;
  text-transform: capitalize;
  text-align: left;
  border-radius: 4px 4px 0 0;
}

.d-table thead th {
  color: #728195;
  text-transform: uppercase;
}

.d-table thead th,
.d-table tbody td {
  padding: 0.5rem;
  border: 1px solid #d8d8d8;
  line-height: 1;
  font-size: 10px;
  width: 85px;
  word-break: break-word;
}

.d-table thead th:first-child {
  width: 100px;
}

.subs-today__event-title {
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.date-buttons.bttn--disabled {
  pointer-events: none;
}

.headline-date {
  font-size: 1.65rem;
}

.users-today__slider-pager {
  text-align: center;
  font-size: 1.65rem;
  font-weight: bold;
  color: #728195;
}

.d-progress {
  margin: 0.5rem auto;
  position: relative;
  width: 100px;
  height: 1rem;
  background: #fff;
  border-radius: 0.25rem;
}

.d-progress__bar {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 1px;
  background: linear-gradient(90deg,#4553ff,#7f7fd5);
  border-radius: 0.25rem;
  max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// OverviewToday.vue?bed75296